import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown, ChevronRight, ExternalLink, Loader2, Save, Search } from "lucide-react";
import { logisticsApi } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STATUS_OPTIONS = [
  { value: "todo", label: "À faire" },
  { value: "requested", label: "Demandé" },
  { value: "accepted", label: "Accepté" },
  { value: "in_delivery", label: "En livraison" },
  { value: "delivered", label: "Livré" },
];

const STATUS_BADGE = {
  todo: "bg-orange-100 text-orange-800",
  requested: "bg-slate-200 text-slate-800",
  accepted: "bg-blue-100 text-blue-800",
  in_delivery: "bg-amber-100 text-amber-800",
  delivered: "bg-emerald-100 text-emerald-800",
};

const DELIVERY_PLACE_OPTIONS = [
  { value: "warehouse", label: "À l'entrepôt" },
  { value: "office", label: "Au bureau" },
  { value: "site", label: "Sur le chantier" },
  { value: "supplier", label: "Chez le fournisseur" },
  { value: "other", label: "Autre" },
];

const DELIVERY_PLACE_LABEL = Object.fromEntries(DELIVERY_PLACE_OPTIONS.map((o) => [o.value, o.label]));

function FragmentRow({ r, isOpen, setExpandedId, changeStatus, renderPlace, onOpenChange, navigate, setRows }) {
  return (
    <>
      <tr
        className="border-t hover:bg-slate-50"
        data-testid={`global-todo-row-${r.item_id}`}
      >
        <td className="px-2 py-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => setExpandedId(isOpen ? null : r.item_id)}
            data-testid={`global-todo-expand-${r.item_id}`}
          >
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </td>
        <td className="px-3 py-2 font-mono text-xs">{r.order_reference || r.order_id.slice(0, 8)}</td>
        <td className="px-3 py-2 truncate max-w-[140px]">{r.client_name || "—"}</td>
        <td className="px-3 py-2">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: r.category_color || "#94a3b8" }} />
            {r.category_name}
            {r.est_interne_administratif && (
              <Badge variant="secondary" className="text-[10px]">admin</Badge>
            )}
          </span>
        </td>
        <td className="px-3 py-2">{r.label}</td>
        <td className="px-3 py-2 text-center">{r.quantity || 1}</td>
        <td className="px-3 py-2">
          <Select value={r.status} onValueChange={(v) => changeStatus(r, v)}>
            <SelectTrigger
              className={`h-7 text-xs w-[130px] ${STATUS_BADGE[r.status] || ""}`}
              data-testid={`global-todo-status-${r.item_id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </td>
        <td className="px-3 py-2 text-xs whitespace-nowrap">{r.delivery_due_date || "—"}</td>
        <td className="px-3 py-2 text-xs whitespace-nowrap">{r.delivery_announced_date || "—"}</td>
        <td className="px-3 py-2 text-xs">{renderPlace(r) || "—"}</td>
        <td className="px-3 py-2 text-xs">
          {r.worker_pickup_required
            ? <Badge className="bg-orange-100 text-orange-800">Oui</Badge>
            : <span className="text-slate-400">—</span>}
        </td>
        <td className="px-3 py-2 text-right">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => { onOpenChange(false); navigate(`/commandes/${r.order_id}`); }}
            data-testid={`global-todo-open-${r.item_id}`}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        </td>
      </tr>
      {isOpen && (
        <tr className="border-t bg-slate-50">
          <td colSpan={12} className="p-0">
            <GlobalItemDetails
              row={r}
              onSaved={(updated) => {
                setRows((rs) => rs.map((x) => x.item_id === r.item_id ? { ...x, ...updated } : x));
              }}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Carte d'édition rapide en ligne d'un item logistique global.
 * Permet de modifier statut, dates, lieu, pickup directement depuis la
 * vue globale "À traiter et commander".
 */
function GlobalItemDetails({ row, onSaved }) {
  const [state, setState] = useState({
    delivery_due_date: row.delivery_due_date || "",
    delivery_announced_date: row.delivery_announced_date || "",
    delivery_place: row.delivery_place || "",
    delivery_place_custom: row.delivery_place_custom || "",
    show_delivery_on_worksheet: !!row.show_delivery_on_worksheet,
    worker_pickup_required: !!row.worker_pickup_required,
    pickup_address: row.pickup_address || "",
    pickup_hours: row.pickup_hours || "",
  });
  const [saving, setSaving] = useState(false);
  const update = (patch) => setState((s) => ({ ...s, ...patch }));

  const save = async () => {
    try {
      setSaving(true);
      const payload = { ...state };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      await logisticsApi.updateOrderLogisticsItem(row.order_id, row.item_id, payload);
      toast.success("Modifications enregistrées");
      await onSaved?.({ ...row, ...state });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-50 p-3 space-y-3" data-testid={`global-todo-details-${row.item_id}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <div className="md:col-span-3">
          <Label className="text-xs">Date à respecter</Label>
          <Input
            type="date"
            value={state.delivery_due_date || ""}
            onChange={(e) => update({ delivery_due_date: e.target.value })}
            data-testid={`global-todo-due-${row.item_id}`}
          />
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs">Date annoncée fournisseur</Label>
          <Input
            type="date"
            value={state.delivery_announced_date || ""}
            onChange={(e) => update({ delivery_announced_date: e.target.value })}
            data-testid={`global-todo-announced-${row.item_id}`}
          />
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs">Lieu de livraison</Label>
          <Select
            value={state.delivery_place || ""}
            onValueChange={(v) => update({ delivery_place: v })}
          >
            <SelectTrigger data-testid={`global-todo-place-${row.item_id}`}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {DELIVERY_PLACE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3 flex items-end">
          <label className="flex items-center gap-2 text-xs">
            <Switch
              checked={state.show_delivery_on_worksheet}
              onCheckedChange={(v) => update({ show_delivery_on_worksheet: v })}
              data-testid={`global-todo-show-delivery-${row.item_id}`}
            />
            Date + lieu sur la fiche
          </label>
        </div>
        {state.delivery_place === "other" && (
          <div className="md:col-span-12">
            <Label className="text-xs">Adresse complète de livraison</Label>
            <Input
              value={state.delivery_place_custom}
              onChange={(e) => update({ delivery_place_custom: e.target.value })}
              data-testid={`global-todo-place-custom-${row.item_id}`}
            />
          </div>
        )}
      </div>

      <div className="border-t pt-2 space-y-2">
        <label className="flex items-center gap-2 text-xs font-medium">
          <Switch
            checked={state.worker_pickup_required}
            onCheckedChange={(v) => update({ worker_pickup_required: v })}
            data-testid={`global-todo-pickup-toggle-${row.item_id}`}
          />
          L&apos;ouvrier doit récupérer
        </label>
        {state.worker_pickup_required && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
            <div className="md:col-span-7">
              <Label className="text-xs">Adresse</Label>
              <Input
                value={state.pickup_address}
                onChange={(e) => update({ pickup_address: e.target.value })}
                data-testid={`global-todo-pickup-address-${row.item_id}`}
              />
            </div>
            <div className="md:col-span-5">
              <Label className="text-xs">Horaires</Label>
              <Input
                value={state.pickup_hours}
                onChange={(e) => update({ pickup_hours: e.target.value })}
                data-testid={`global-todo-pickup-hours-${row.item_id}`}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          onClick={save}
          disabled={saving}
          data-testid={`global-todo-save-${row.item_id}`}
        >
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

/**
 * Modale "À traiter et commander" — vue globale cross-commandes des
 * tags logistiques avec toutes les infos détaillées (date, lieu, pickup)
 * et changement rapide de statut + édition inline.
 */
export default function GlobalLogisticsTodoDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("order");
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [todo, cats] = await Promise.all([
        logisticsApi.getLogisticsTodo(),
        logisticsApi.getLogisticsCategories(),
      ]);
      setRows(todo.data?.items || []);
      setCategories(cats.data || []);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  const changeStatus = async (row, newStatus) => {
    try {
      await logisticsApi.updateOrderLogisticsItem(row.order_id, row.item_id, { status: newStatus });
      setRows((rs) => rs.map((r) => r.item_id === row.item_id ? { ...r, status: newStatus } : r));
    } catch (e) {
      toast.error("Erreur de mise à jour");
    }
  };

  const filtered = useMemo(() => {
    let out = rows;
    if (filterCat !== "all") out = out.filter((r) => r.category_id === filterCat);
    if (filterStatus !== "all") out = out.filter((r) => r.status === filterStatus);
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter((r) =>
        (r.label || "").toLowerCase().includes(q) ||
        (r.order_reference || "").toLowerCase().includes(q) ||
        (r.client_name || "").toLowerCase().includes(q) ||
        (r.pickup_address || "").toLowerCase().includes(q),
      );
    }
    if (sortBy === "order") {
      out = [...out].sort((a, b) => (a.order_reference || "").localeCompare(b.order_reference || ""));
    } else if (sortBy === "category") {
      out = [...out].sort((a, b) => (a.category_name || "").localeCompare(b.category_name || ""));
    } else if (sortBy === "status") {
      const ord = { todo: -1, requested: 0, accepted: 1, in_delivery: 2, delivered: 3 };
      out = [...out].sort((a, b) => (ord[a.status] ?? 0) - (ord[b.status] ?? 0));
    } else if (sortBy === "due_date") {
      out = [...out].sort((a, b) => (a.delivery_due_date || "9999").localeCompare(b.delivery_due_date || "9999"));
    }
    return out;
  }, [rows, filterCat, filterStatus, query, sortBy]);

  const renderPlace = (r) => {
    if (!r.delivery_place) return null;
    if (r.delivery_place === "other") return r.delivery_place_custom || "Autre";
    return DELIVERY_PLACE_LABEL[r.delivery_place];
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="global-logistics-dialog">
        <DialogHeader>
          <DialogTitle>À traiter et commander — vue globale</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Recherche article / commande / client / adresse…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-8"
              data-testid="global-todo-search"
            />
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-[180px]" data-testid="global-todo-filter-cat">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[160px]" data-testid="global-todo-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px]" data-testid="global-todo-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="order">Tri : Commande</SelectItem>
              <SelectItem value="category">Tri : Catégorie</SelectItem>
              <SelectItem value="status">Tri : Statut</SelectItem>
              <SelectItem value="due_date">Tri : Date à respecter</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 overflow-auto border rounded-md mt-2">
          {loading ? (
            <div className="flex items-center justify-center p-6 gap-2 text-sm text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
            </div>
          ) : filtered.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground italic" data-testid="global-todo-empty">
              Aucun besoin logistique à afficher.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="text-left">
                  <th className="px-2 py-2 w-8"></th>
                  <th className="px-3 py-2 font-medium">Commande</th>
                  <th className="px-3 py-2 font-medium">Client</th>
                  <th className="px-3 py-2 font-medium">Catégorie</th>
                  <th className="px-3 py-2 font-medium">Article</th>
                  <th className="px-3 py-2 font-medium text-center">Qté</th>
                  <th className="px-3 py-2 font-medium">Statut</th>
                  <th className="px-3 py-2 font-medium">À respecter</th>
                  <th className="px-3 py-2 font-medium">Annoncée</th>
                  <th className="px-3 py-2 font-medium">Lieu</th>
                  <th className="px-3 py-2 font-medium">Pickup</th>
                  <th className="px-3 py-2 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const isOpen = expandedId === r.item_id;
                  return (
                    <FragmentRow key={r.item_id} r={r} isOpen={isOpen} setExpandedId={setExpandedId} changeStatus={changeStatus} renderPlace={renderPlace} onOpenChange={onOpenChange} navigate={navigate} setRows={setRows} />
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="text-xs text-muted-foreground pt-2">
          {filtered.length} élément(s) affiché(s) sur {rows.length}.
        </div>
      </DialogContent>
    </Dialog>
  );
}
